<template>
  <div class="grid grid-cols-2 gap-2 pt-1 normal-case">
    <InfuseTag
      v-for="parkingRestriction in ParkingRestriction"
      :key="`evse-${evseId}-parking-restriction-${parkingRestriction}`"
      :clickable="!readOnly"
      :variant="isSelectedParkingRestriction(parkingRestriction) ? 'primary' : 'neutral'"
      @click="selectParkingRestriction(parkingRestriction)"
    >
      <font-awesome-icon
        v-if="isSelectedParkingRestriction(parkingRestriction)"
        :fixed-width="true"
        :icon="['fas', 'check']"
        class="mr-1"
      />
      <font-awesome-icon v-else :icon="['fas', 'xmark']" class="mr-1" />
      {{ t(`parkingRestrictions.options.${parkingRestriction}`) }}
    </InfuseTag>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ParkingRestriction } from '@infuse/cpms/charging-station/store';
import { InfuseTag } from '@infuse/ui/components';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface Emits {
  (e: 'select', parkingRestriction: ParkingRestriction): void;
}
const emit = defineEmits<Emits>();

interface Props {
  evseId: number;
  readOnly: boolean;
  selectedParkingRestrictions: ParkingRestriction[];
}
const props = withDefaults(defineProps<Props>(), {
  readOnly: false
});

const { t } = useI18n();

const parkingRestrictions = ref<ParkingRestriction[]>([]);

onMounted(() => {
  parkingRestrictions.value = props.selectedParkingRestrictions;
});

watch(
  // This being a function + 'deep: true' makes this 'watch' able to track changes in an array.
  () => props.selectedParkingRestrictions,
  async (inputParkingRestrictions) => {
    // If an external source updates the parking restrictions (e.g. model reload),
    // then reflect those changes in this component as well.
    parkingRestrictions.value = inputParkingRestrictions;
  },
  { immediate: false, deep: true }
);

const selectParkingRestriction = (parkingRestriction: ParkingRestriction) => {
  if (!props.readOnly) {
    emit('select', parkingRestriction);
  }
};

const isSelectedParkingRestriction = (parkingRestriction: ParkingRestriction): boolean => {
  return !!parkingRestrictions.value?.find((pr: ParkingRestriction) => pr === parkingRestriction);
};
</script>

<i18n>
{
  "en": {
    "parkingRestrictions": {
      "options": {
        "CUSTOMERS": "For customers / guests only",
        "DISABLED": "For disabled only",
        "EV_ONLY": "EVs only",
        "MOTORCYCLES": "Motorcycles / scooters only",
        "PLUGGED": "Plugged in required"
      }
    }
  },
  "nl": {
    "parkingRestrictions": {
      "options": {
        "CUSTOMERS": "Enkel voor klanten / gasten",
        "DISABLED": "Enkel voor gehandicapten",
        "EV_ONLY": "Enkel voor EVs",
        "MOTORCYCLES": "Enkel motoren / scooters",
        "PLUGGED": "Moet ingeplugd zijn"
      }
    }
  }
}
</i18n>
